import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import { loadStatus } from "../util";

export const getCommunities = createAsyncThunk(
  "communities/getCommunities",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().communities;
      if (state.count.getCommunities <= 1) {
        const res = await axios.get("/communities", { params: reqData });
        return res.data;
      }
      return { data: state.data, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getCountryCommunities = createAsyncThunk(
  "communities/getCountryCommunities",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().communities;
      if (state.count.getCountryCommunities <= 1) {
        const res = await axios.get("/country-communities/" + reqData);
        return res.data;
      }
      return { data: state.data, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getRegisterCountryCommunities = createAsyncThunk(
  "communities/getRegisterCountryCommunities",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().communities;
      if (state.count.getRegisterCountryCommunities <= 1) {
        const res = await axios.get("/register-country-communities/" + reqData);
        return res.data;
      }
      return state.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getCommunity = createAsyncThunk(
  "communities/getCommunity",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().communities;
      const res = await axios.get("/community/" + reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const addCommunity = createAsyncThunk(
  "communities/addCommunity",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-community", reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const editCommunity = createAsyncThunk(
  "communities/editCommunity",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.put("/edit-community/" + id, reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const deleteCommunity = createAsyncThunk(
  "communities/deleteCommunity",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.delete("/delete-community/" + id, {
        params: data,
      });
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const communitiesSlice = createSlice({
  name: "communities",
  initialState: {
    total: 0,
    data: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getCommunities: false,
      getCommunity: false,
      addCommunity: false,
      editCommunity: false,
      deleteCommunity: false,
      getCountryCommunities: false,
      getRegisterCountryCommunities: false,
    },
    count: {
      getCommunities: 0,
      getCommunity: 0,
      getCountryCommunities: 0,
      getRegisterCountryCommunities: 0,
    },
  },
  reducers: {
    setCommunity: (state, action) => {
      state.data = action.payload;
    },
    setSelectedCommunity: (state, action) => {
      state.selected = action.payload;
    },
    unsetSelectedCommunity: (state) => {
      state.selected = {};
    },
    unsetCommunity: (state) => {
      state.data = [];
      state.selected = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Communities
      .addCase(getCommunities.pending, (state) => {
        state.load.getCommunities = loadStatus.loading;
        state.error = null;
        state.count.getCommunities = ++state.count.getCommunities;
      })
      .addCase(getCommunities.fulfilled, (state, action) => {
        state.load.getCommunities = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getCommunities = 0;
      })
      .addCase(getCommunities.rejected, (state, action) => {
        state.load.getCommunities = loadStatus.error;
        state.error = action.error;
        state.count.getCommunities = 0;
      })
      //Get all Country Communities
      .addCase(getCountryCommunities.pending, (state) => {
        state.load.getCountryCommunities = loadStatus.loading;
        state.error = null;
        state.count.getCountryCommunities = ++state.count.getCountryCommunities;
      })
      .addCase(getCountryCommunities.fulfilled, (state, action) => {
        state.load.getCountryCommunities = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getCountryCommunities = 0;
      })
      .addCase(getCountryCommunities.rejected, (state, action) => {
        state.load.getCountryCommunities = loadStatus.error;
        state.error = action.error;
        state.count.getCountryCommunities = 0;
      })
      //Get all Country Communities unlocked for register with org_id
      .addCase(getRegisterCountryCommunities.pending, (state) => {
        state.load.getRegisterCountryCommunities = loadStatus.loading;
        state.error = null;
        state.count.getRegisterCountryCommunities = ++state.count
          .getRegisterCountryCommunities;
      })
      .addCase(getRegisterCountryCommunities.fulfilled, (state, action) => {
        state.load.getRegisterCountryCommunities = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getRegisterCountryCommunities = 0;
      })
      .addCase(getRegisterCountryCommunities.rejected, (state, action) => {
        state.load.getRegisterCountryCommunities = loadStatus.error;
        state.error = action.error;
        state.count.getRegisterCountryCommunities = 0;
      })
      //Get Project
      .addCase(getCommunity.pending, (state) => {
        state.load.getCommunity = loadStatus.loading;
        state.count.getCommunity = ++state.count.getCommunity;
        state.error = null;
      })
      .addCase(getCommunity.fulfilled, (state, action) => {
        state.load.getCommunity = loadStatus.success;
        state.selected = action.payload;
        state.count.getCommunity = 0;
      })
      .addCase(getCommunity.rejected, (state, action) => {
        state.load.getCommunity = loadStatus.error;
        state.error = action.error;
        state.count.getCommunity = 0;
      })
      //Add Project
      .addCase(addCommunity.pending, (state) => {
        state.load.addCommunity = loadStatus.loading;
        state.error = null;
      })
      .addCase(addCommunity.fulfilled, (state, action) => {
        state.load.addCommunity = loadStatus.success;
        state.data.unshift(action.payload);
      })
      .addCase(addCommunity.rejected, (state, action) => {
        state.load.addCommunity = loadStatus.error;
        state.error = action.error;
      })
      //Update Project
      .addCase(editCommunity.pending, (state) => {
        state.load.editCommunity = loadStatus.loading;
        state.error = null;
      })
      .addCase(editCommunity.fulfilled, (state, action) => {
        state.load.editCommunity = loadStatus.success;
        state.selected = action.payload;
        const index = state.data.findIndex(
          (item) => item.id === action.payload.id
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editCommunity.rejected, (state, action) => {
        state.load.editCommunity = loadStatus.error;
        state.error = action.error;
      })
      //Delete Project
      .addCase(deleteCommunity.pending, (state) => {
        state.load.deleteCommunity = loadStatus.loading;
        state.error = null;
      })
      .addCase(deleteCommunity.fulfilled, (state, action) => {
        state.load.deleteCommunity = loadStatus.success;
        state.data.filter((project) => project.id !== action.meta.arg.id);
      })
      .addCase(deleteCommunity.rejected, (state, action) => {
        state.load.deleteCommunity = loadStatus.error;
        state.error = action.error;
      });
  },
});

export const {
  setCommunity,
  setSelectedCommunity,
  unsetCommunity,
  unsetSelectedCommunity,
} = communitiesSlice.actions;
export default communitiesSlice.reducer;
